import { FC, createContext, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Beneficiary } from 'types';
import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetSocietyValue } from 'modules/society/hooks/useGetSocietyValue';

import { beneficiarySellModalInitValue } from '../constants/beneficiarySellModalInitValue';

import { BeneficiarySellModalContextType } from '../types/BeneficiarySellModalContextType';
import { useGetUnitsPrice } from '../hooks/useGetUnitsPrice';
import { useGetTotals } from '../hooks/useGetTotals';

const BeneficiarySellModalContext =
  createContext<BeneficiarySellModalContextType>(beneficiarySellModalInitValue);

export const BeneficiarySellModalProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
  beneficiary: Beneficiary;
  beneficiaryAllPlans: any[];
  props: any;
}> = ({ children, beneficiary, beneficiaryAllPlans, ...props }) => {
  const dispatch = useDispatch();

  const { societyValue } = useGetSocietyValue().data;

  const [simulatedSocietyValue, setSimulatedSocietyValue] =
    useState<number>(societyValue);
  const [simulatedRetentionPercent, setSimulatedRetentionPercent] =
    useState<number>(0);
  const [includeAllUnits, setIncludeAllUnits] = useState<boolean>(true);
  const [
    includeBeneficiaryNotVestedUnits,
    setIncludeBeneficiaryNotVestedUnits,
  ] = useState<boolean>(false);

  const { unitsPrice } = useGetUnitsPrice({
    includeAllUnits,
    simulatedSocietyValue,
  }).data;

  const { simulatedTotal, retentionTotal } = useGetTotals({
    beneficiaryAllPlans,
    unitsPrice,
    simulatedRetentionPercent,
    includeBeneficiaryNotVestedUnits,
  }).data;

  useEffect(() => {
    setSimulatedSocietyValue(societyValue);
  }, [societyValue]);

  const handleChangeSimulatedSocietyValue = (
    // eslint-disable-next-line no-undef
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSimulatedSocietyValue(Number(event.target.value));
  };

  const handleChangeSimulatedRetentionPercent = (
    // eslint-disable-next-line no-undef
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSimulatedRetentionPercent(Number(event.target.value));
  };

  const handleChangeIncludeAllUnits = () => {
    setIncludeAllUnits((prev) => !prev);
  };

  const handleChangeIncludeBeneficiaryNotVestedUnits = () => {
    setIncludeBeneficiaryNotVestedUnits((prev) => !prev);
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  return (
    <BeneficiarySellModalContext.Provider
      value={{
        data: {
          beneficiary,
          beneficiaryAllPlans,
          societyValue,
          simulatedSocietyValue,
          simulatedRetentionPercent,
          simulatedTotal,
          retentionTotal,
          unitsPrice,
          includeAllUnits,
          includeBeneficiaryNotVestedUnits,
        },
        actions: {
          handleCloseModal,
          handleChangeSimulatedSocietyValue,
          handleChangeSimulatedRetentionPercent,
          handleChangeIncludeAllUnits,
          handleChangeIncludeBeneficiaryNotVestedUnits,
        },
      }}
    >
      {children}
    </BeneficiarySellModalContext.Provider>
  );
};

export const useBeneficiarySellModal = () => {
  const context = useContext(BeneficiarySellModalContext);
  if (!context) {
    throw new Error(
      'useBeneficiarySellModal must be used within a BeneficiarySellModalProvider'
    );
  }
  return context;
};
